import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu'; // Hungarian locale import
import './BookingDetails.css'; // CSS for this component
import { apiRequest } from './utils'; // Ensure correct path
import translations from './lang';

const BookingDetails = () => {
    const { userId, serviceId, bookingDate } = useParams(); // Extract parameters from the URL
    const [profileData, setProfileData] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [timeSlot, setTimeSlot] = useState('01:00'); // Default time slot
    const [showPackageModal, setShowPackageModal] = useState(false); // Csomagválasztó modális
    const [selectedAppointment, setSelectedAppointment] = useState(null); // Kiválasztott időpont
    const [selectedService, setSelectedService] = useState(null); // Kiválasztott szolgáltatás
    const [services, setServices] = useState([]); // Szolgáltatások listája
    const language = localStorage.getItem('language') || 'ro';
    const t = translations[language];
    const [appointmentPackages, setAppointmentPackages] = useState([]);
    const [currentService, setCurrentService] = useState(null); // Track selected service
    const [selectedEvent, setSelectedEvent] = useState(null); // Kiválasztott setSelectedEvent
    
    
    useEffect(() => {
            const loadAppointmentPackages = async () => {
              if (!showPackageModal || !selectedAppointment?.id) return;
          
              try {
                const res = await apiRequest(
                  `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
                  {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      'Accept-Language': localStorage.getItem('language') || 'ro',
                    },
                  }
                );
                const data = await res.json();
                setAppointmentPackages(Array.isArray(data) ? data : []);
              } catch (e) {
                console.error('Packages fetch error:', e);
                setAppointmentPackages([]);
              }
            };
          
            loadAppointmentPackages();
          }, [showPackageModal, selectedAppointment]);  
        
    useEffect(() => {
        // Decode JWT token to extract current user ID
        const token = localStorage.getItem('access_token');
        if (token) {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join('')
            );
            const payload = JSON.parse(jsonPayload);
            setCurrentUserId(payload.user_id);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            // Fetch user profile
            apiRequest(`${process.env.REACT_APP_API}/api/user-profile/${userId}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            })
                .then((response) => response.json())
                .then((data) => setProfileData(data))
                .catch((error) => {
                    console.error('Error fetching profile data:', error);
                    setError('Error fetching profile data');
                })
                .finally(() => setLoading(false));

            // Fetch services
            fetchServices(userId);

            // Fetch appointments for the service
            fetchAppointments();
            fetchServiceInterval(serviceId);
        }
    }, [userId, serviceId]);

    const fetchServices = (userId) => {
        fetch(`${process.env.REACT_APP_API}/api/user-services-by-user-id/${userId}/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language  // <== nyelv küldése headerként
            },
        })
            .then((response) => response.json())
            .then((data) => setServices(data.data))
            .catch((error) => console.error('Error fetching services:', error));
    };

    const fetchServiceInterval = (serviceId) => {
        fetch(`${process.env.REACT_APP_API}/api/service/${serviceId}/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language  // <== nyelv küldése headerként
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (data && data.interval) {
                    setTimeSlot(data.interval); // Set the interval dynamically based on service
                }
            })
            .catch((error) => console.error('Error fetching service interval:', error));
    };

    const fetchAppointments = () => {
        if (serviceId) {
            fetch(`${process.env.REACT_APP_API}/api/appointments-by-service-id/${serviceId}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    'Accept-Language': language  // <== nyelv küldése headerként
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    const formattedAppointments = data.data.map((event) => {
                        const bookedCount = event.bookings ? event.bookings.length : 0;
                        const maxCapacity = event.max_capacity || 1;
                    
                        const userBooking = event.bookings.find(
                            (booking) => booking.user === parseInt(userId)
                        );
                    
                        // Csak saját foglalás szimbólumai és színe
                        let statusSymbol = '';
                        let color = '';
                    
                        if (userBooking) {
                            if (userBooking.status === 'Confirmed' || userBooking.status === 'confirmed') {
                                statusSymbol = ' ✅';
                                color = 'green';
                            } else if (userBooking.status === 'Rejected' || userBooking.status === 'rejected') {
                                statusSymbol = ' ❌';
                                color = '#f28b82';
                            } else if (userBooking.status === 'Pending' || userBooking.status === 'pending') {
                                statusSymbol = ' ⚠️';
                                color = 'orange';
                            }
                        }
                    
                        return {
                            id: event.id,
                            title: `${event.service_name} - ${bookedCount}/${maxCapacity}${statusSymbol}`,
                            start: new Date(event.start_time),
                            end: new Date(event.end_time),
                            bookings: event.bookings || [],
                            color: userBooking ? color : '', // csak saját színt adunk, ha foglalta
                        };
                    });                    
                    setAppointments(formattedAppointments);
                })
                .catch((error) => console.error('Error fetching appointments:', error));
        }
    };    

    // 💡 FIGYELEM: kívül már van language és t (translations[language]) — nem kell újra definiálni

const handlePackageSelect = async (appointmentId, packageId) => {
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API}/api/appointments/${appointmentId}/book/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Accept-Language': language,
          },
          body: JSON.stringify({ package_id: packageId }),
        }
      );
  
      const data = await res.json();
  
      if (res.ok && data?.success) {
        // Frissítjük a naptár eseményeit
        await fetchAppointments(currentService || parseInt(serviceId, 10));
  
        // Frissítjük a modál tetején lévő csomaglistát
        if (selectedAppointment?.id) {
          const pkRes = await apiRequest(
            `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language,
              },
            }
          );
          const pkData = await pkRes.json();
          setAppointmentPackages(Array.isArray(pkData) ? pkData : []);
        }
  
        alert(t.booking_success);
      } else {
        alert(data?.error || t.booking_failed);
      }
    } catch (err) {
      console.error('Hiba a foglalás során:', err);
      alert(t.booking_error);
    }
  };
  
  
  async function handlePackageRemove(packageId) {
    const ok = window.confirm(t.confirm_delete_package);
    if (!ok) return;
  
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API}/api/appointment-packages/${packageId}/delete/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Accept-Language': language,
          },
        }
      );
  
      // lehet, hogy 204 No Content jön vissza; próbáljuk olvasni, de ne dőljünk el tőle
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        // nincs body (pl. 204) → tekintsük sikeresnek, ha res.ok
      }
  
      if (res.ok && (data?.success ?? true)) {
        alert(t.package_deleted_success);
  
        // Frissítjük a modál tetején lévő csomaglistát
        if (selectedAppointment?.id) {
          const pkRes = await apiRequest(
            `${process.env.REACT_APP_API}/api/appointment-packages/${selectedAppointment.id}/`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language,
              },
            }
          );
          const pkData = await pkRes.json();
          setAppointmentPackages(Array.isArray(pkData) ? pkData : []);
        }
      } else {
        alert(data?.error || t.package_deleted_error);
      }
    } catch (err) {
      console.error('Hiba a csomag törlésekor:', err);
      alert(t.package_deleted_error);
    }
  }    
      
    async function handleDeleteBooking(appointmentId) {
        const confirmDelete = window.confirm(t.confirm_cancel_booking);
        if (!confirmDelete) return;
      
        try {
          const res = await apiRequest(`${process.env.REACT_APP_API}/api/appointments/${appointmentId}/unbook/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Accept-Language': language
            },
            body: JSON.stringify({ booked_by: currentUserId })
          });
      
          // ha a helpered a fetch-re wrap, akkor lehet, hogy itt nincs .json() hívásra szükség
          const data = await res.json?.() ?? {};
      
          if (res.ok && (data.success ?? true)) {
            alert(t.booking_cancelled_success);
            await fetchAppointments(currentService || parseInt(serviceId, 10));
            setSelectedAppointment(null);
            setShowPackageModal(false);
          } else {
            // ha a szerver jogosultsági hibát ad
            if (res.status === 403) {
              alert(t.not_booked_by_you);
            } else {
              alert(data.error || t.booking_cancelled_error);
            }
          }
        } catch (e) {
          console.error('Hiba a foglalás törlése során:', e);
          alert(t.booking_cancelled_error);
        }
      }   

    const handleEventClick = (clickInfo) => {
        const event = clickInfo.event;
        const appointmentId = event.id;
    
        
        //const confirmBooking = window.confirm(t.confirm_booking); // << MEGERŐSÍTÉS
        //if (!confirmBooking) return;

        const selectedService = services.find(service => service.id === (currentService || parseInt(serviceId)));
        setSelectedService(selectedService);
        setSelectedAppointment({ id: appointmentId, title: event.title });
        setSelectedEvent(event)
        setShowPackageModal(true);
        
    };

    const handleIntervalChange = (e) => {
        setTimeSlot(e.target.value);
    };

    const calendarOptions = {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        initialDate: bookingDate, // Set the calendar's initial date to the booking date
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridWeek,timeGridDay',
        },
        views: {
            timeGridWeek: {
                dayHeaderFormat: { weekday: 'short', day: 'numeric' }, // Csak a nap számát és a hét rövidített nevét jeleníti meg
            },
        },
        events: appointments,
        selectable: false,
        selectMirror: false,
        dayMaxEvents: true,
        slotDuration: timeSlot, // Dynamically update slot duration
        slotLabelInterval: timeSlot,
        slotMinTime: '06:00:00',
        slotMaxTime: '24:00:00',
        allDaySlot: false,
        locale: language,
        buttonText: {
            today: t.today,
            month: t.month,
            week: t.week,
            day: t.day,
          },  
        firstDay: 1,
        height: 'auto',
        eventClick: handleEventClick,
        eventClassNames: function (arg) {
            const userBooking = arg.event.extendedProps.bookings.find(
                (booking) => booking.user === currentUserId
            );
        
            if (!userBooking) return [];
        
            return ['booked-event', 'user-booked'];
        }
    };

    if (loading) {
        return <div>{t.loading_data}</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="booking-details-container3">
        {profileData && profileData.user_data && (
        <div className="profile-container3">
          <div className="profile-details3">
            <p>
              <strong>{t.name || 'Név'}:</strong>{' '}
              {profileData.user_data.first_name} {profileData.user_data.last_name}
            </p>
          </div>
        </div>
      )}
      
            <hr />

            {/* <div className="slot-select">
                <label>Időintervallum kiválasztása:</label>
                <select onChange={handleIntervalChange} value={timeSlot}>
                    <option value="00:15">15 perc</option>
                    <option value="00:20">20 perc</option>
                    <option value="00:30">30 perc</option>
                    <option value="01:00">1 óra</option>
                </select>
            </div> */}

            <FullCalendar {...calendarOptions} />

            {showPackageModal && (
                <div className="modal">
                    <div className="modal-content">
                    {/* Bezáró ikon */}
                    <button className="close-icon" onClick={() => setShowPackageModal(false)}>×</button>

                    <h4>{t.select_package}</h4>

                    {/* TÁBLÁZAT: Hozzárendelt csomagok listázása törlés gombbal */}
                    {appointmentPackages?.length > 0 ? (
                        <table className="package-table">
                        <thead>
                            <tr>
                            <th>{t.name}</th>
                            <th>{t.description}</th>
                            <th>{t.price}</th>
                            <th>{t.duration}</th>
                            <th>{t.action}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {appointmentPackages.map(pkg => (
                            <tr key={pkg.id}>
                                <td>{pkg.service_package_name}</td>
                                <td>{pkg.service_package_description}</td>
                                <td>{pkg.service_package_price} RON</td>
                                <td>
                                {Math.floor(pkg.service_package_duration)} {t.hours}{" "}
                                {Math.round((pkg.service_package_duration % 1) * 60)} {t.minutes}
                                </td>
                                <td>
                                <button onClick={() => handlePackageRemove(pkg.id)}>
                                    {t.delete}
                                </button>
                                </td>
                            </tr>
                            ))}
                        </tbody>
                        </table>
                    ) : (
                        <p>{t.no_packages_for_appointment}</p>
                    )}

                    {/* A meglévő kártyás csomaglista */}
                    {selectedService?.packages?.length > 0 ? (
                        selectedService.packages.map(pkg => (
                        <div key={pkg.id} className="package-item">
                            <p><strong>{pkg.name}</strong></p>
                            <p>{pkg.description}</p>
                            <p>{t.price}: {pkg.price} RON</p>
                            <p>
                            {t.duration}: {Math.floor(pkg.duration)} {t.hours}{" "}
                            {Math.round((pkg.duration % 1) * 60)} {t.minutes}
                            </p>
                            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                            <button onClick={() => handlePackageSelect(selectedAppointment.id, pkg.id)}>
                                {t.choose}
                            </button>
                            </div>
                        </div>
                        ))
                    ) : (
                        <p>{t.no_packages_for_appointment}</p>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                        <button onClick={() => handleDeleteBooking(selectedAppointment.id)}>
                        {t.delete_booking}
                        </button>
                    </div>
                    </div>
                </div>
                )}
        </div>
    );
};

export default BookingDetails;
